import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import authStore from '../store/authStore';
import { track } from '../utils/analytics';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';
  const { register, loading, error } = authStore();
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', userType: 'seeker', showEmail: true, showPhone: true });
  const [showPw, setShowPw] = useState(false);
  const [localError, setLocalError] = useState('');

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (!form.name || !form.email || !form.password) { setLocalError('Compila tutti i campi'); return; }
    if (form.password.length < 6) { setLocalError('La password deve avere almeno 6 caratteri'); return; }
    if (form.password !== form.confirmPassword) { setLocalError('Le password non corrispondono'); return; }
    try {
      track('signup_start', { user_type: form.userType });
      const res = await register(form.name, form.email, form.password, form.userType, form.showEmail, form.showPhone);
      if (res?.session_token) {
        localStorage.setItem('session_token', res.session_token);
      }
      track('signup_success', { user_type: form.userType });
      navigate(redirectTo);
    } catch {
      track('signup_error');
      setLocalError('Errore durante la registrazione');
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4 py-8" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="https://customer-assets.emergentagent.com/job_makehome-frontend/artifacts/pnxu5fqe_6.png" alt="MakeHome 360" className="h-12 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Crea il tuo account</h1>
          <p className="text-slate-400 text-sm mt-1">Unisciti a MakeHome 360</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-xl">
          {(localError || error) && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-xl mb-4" data-testid="register-error">{localError || error}</div>
          )}

          {/* User type selector */}
          <div className="flex gap-2 mb-5">
            <button onClick={() => set('userType', 'seeker')} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition ${form.userType === 'seeker' ? 'bg-[#10B981] text-white' : 'bg-gray-100 text-gray-600'}`} data-testid="type-seeker">Privato</button>
            <button onClick={() => set('userType', 'professional')} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition ${form.userType === 'professional' ? 'bg-[#10B981] text-white' : 'bg-gray-100 text-gray-600'}`} data-testid="type-professional">Professionista</button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input type="text" value={form.name} onChange={e => set('name', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#10B981] focus:border-transparent outline-none" placeholder="Il tuo nome" data-testid="register-name" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#10B981] focus:border-transparent outline-none" placeholder="La tua email" data-testid="register-email" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)} className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#10B981] focus:border-transparent outline-none" placeholder="Minimo 6 caratteri" data-testid="register-password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Conferma Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input type={showPw ? 'text' : 'password'} value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#10B981] focus:border-transparent outline-none" placeholder="Conferma password" data-testid="register-confirm" />
              </div>
            </div>

            {/* Privacy settings */}
            <div className="bg-gray-50 rounded-xl p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-600">Preferenze privacy</p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.showEmail} onChange={e => set('showEmail', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-[#10B981] focus:ring-[#10B981]" data-testid="register-show-email" />
                <span className="text-sm text-gray-600">Mostra la mia email sul profilo</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.showPhone} onChange={e => set('showPhone', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-[#10B981] focus:ring-[#10B981]" data-testid="register-show-phone" />
                <span className="text-sm text-gray-600">Mostra il mio telefono sul profilo</span>
              </label>
            </div>

            <button type="submit" disabled={loading} className="w-full py-3 bg-[#10B981] hover:bg-[#059669] text-white font-semibold rounded-xl transition disabled:opacity-50" data-testid="register-submit">
              {loading ? 'Registrazione...' : 'Registrati'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-5">
            Hai già un account? <Link to="/login" className="text-[#10B981] font-semibold hover:underline">Accedi</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
